/**
 * Vooice v3 — design primitives.
 *
 * Five composable building blocks. Add to this file only when a sixth+
 * primitive is needed by 2+ pages — premature primitives ossify the design.
 *
 * Tokens (declared in tailwind.config + index.css): bg, t1-4, line, line-soft,
 * amber, plum, rose, paper. Fonts: font-display (Fraunces), font-sans (Geist),
 * font-mono (Geist Mono).
 *
 * Contrast guidance: t1 → body, t2 → secondary readable, t3/t4 → decoration.
 */
import { type ReactNode, type ButtonHTMLAttributes, forwardRef } from "react";

const cx = (...c: (string | false | undefined | null)[]) =>
  c.filter(Boolean).join(" ");

// ── Mono caps label ────────────────────────────────────────────────────────
// Eyebrows, status pills, hairline metadata. Always uppercase, wide-tracked.

interface MonoProps {
  children: ReactNode;
  className?: string;
  size?: "xs" | "sm";
  tone?: "t1" | "t2" | "t3" | "amber";
}

export function Mono({
  children,
  className,
  size = "xs",
  tone = "t3",
}: MonoProps) {
  const sizeCls = size === "sm" ? "text-[11px] tracking-[0.16em]" : "text-[10px] tracking-[0.18em]";
  const toneCls = {
    t1: "text-t1",
    t2: "text-t2",
    t3: "text-t3",
    amber: "text-amber",
  }[tone];
  return (
    <span
      className={cx(
        "font-mono font-medium uppercase",
        sizeCls,
        toneCls,
        className,
      )}
    >
      {children}
    </span>
  );
}

// ── LiveDot — pulsing amber indicator ──────────────────────────────────────
// Used in nav (system live), in headers (call active), inline next to mono labels.

interface LiveDotProps {
  size?: number;
  tone?: "amber" | "rose" | "plum" | "t3";
  className?: string;
}

export function LiveDot({ size = 6, tone = "amber", className }: LiveDotProps) {
  const colorMap = {
    amber: "bg-amber shadow-[0_0_14px_rgba(255,179,112,0.55)]",
    rose: "bg-rose shadow-[0_0_14px_rgba(201,128,106,0.55)]",
    plum: "bg-plum",
    t3: "bg-t3",
  };
  return (
    <span
      aria-hidden="true"
      className={cx(
        "rounded-full inline-block animate-pulse-amber",
        colorMap[tone],
        className,
      )}
      style={{ width: size, height: size }}
    />
  );
}

// ── Button — three variants (amber, ghost, paper) + two sizes ──────────────

type Variant = "amber" | "ghost" | "paper";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Render as a link instead of a button. */
  as?: "button" | "a";
  href?: string;
}

const sizeStyles: Record<Size, string> = {
  sm: "px-4 py-2.5 text-xs",
  md: "px-5 py-3 text-sm",
  lg: "px-7 py-4 text-base",
};

const variantStyles: Record<Variant, string> = {
  amber:
    "bg-amber border-amber text-[#2A1A0E] shadow-amber-glow hover:shadow-amber-glow-lg hover:-translate-y-px",
  paper:
    "bg-t1 border-t1 text-bg shadow-[0_1px_0_rgba(255,255,255,0.4)_inset,_0_14px_30px_-14px_rgba(255,255,255,0.18)] hover:-translate-y-px",
  ghost:
    "bg-transparent border-line text-t1 hover:border-t2 hover:bg-white/5",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    { variant = "ghost", size = "md", className, as = "button", href, children, ...rest },
    ref,
  ) {
    const cls = cx(
      "inline-flex items-center justify-center gap-2.5 rounded-pill border font-medium tracking-tight",
      "transition-[transform,box-shadow,background-color,border-color] duration-150",
      "disabled:opacity-50 disabled:pointer-events-none",
      sizeStyles[size],
      variantStyles[variant],
      className,
    );
    if (as === "a" && href) {
      return (
        <a href={href} className={cls}>
          {children}
        </a>
      );
    }
    return (
      <button ref={ref} className={cls} {...rest}>
        {children}
      </button>
    );
  },
);

// ── Pill — small inline status badge ───────────────────────────────────────
// E.g. "BOOKED", "DECLINED", "LIVE", "PENDING". Tone communicates outcome.

type PillTone = "amber" | "rose" | "plum" | "neutral" | "ok" | "warn";

interface PillProps {
  children: ReactNode;
  tone?: PillTone;
  className?: string;
}

const pillTones: Record<PillTone, string> = {
  amber: "bg-amber/10 text-amber border-amber/30",
  rose: "bg-rose/10 text-rose border-rose/30",
  plum: "bg-plum/20 text-[#C4A6D6] border-plum/40",
  neutral: "bg-white/5 text-t2 border-line",
  ok: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  warn: "bg-amber-deep/15 text-amber-deep border-amber-deep/30",
};

export function Pill({ children, tone = "neutral", className }: PillProps) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-1",
        "font-mono text-[10px] font-semibold tracking-[0.14em] uppercase",
        pillTones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

// ── Card — bordered panel with optional glass surface ──────────────────────

interface CardProps {
  children: ReactNode;
  className?: string;
  /** Frosted glass with backdrop-blur. Use sparingly — expensive to render. */
  glass?: boolean;
  /** Soft amber radial highlight in the top-right. Used for hero/feature cards. */
  glow?: boolean;
  /** Padding preset. */
  padding?: "none" | "sm" | "md" | "lg";
}

const padMap = {
  none: "p-0",
  sm: "p-4",
  md: "p-6",
  lg: "p-8",
};

export function Card({
  children,
  className,
  glass = false,
  glow = false,
  padding = "md",
}: CardProps) {
  return (
    <div
      className={cx(
        "rounded-2xl border border-line",
        glass
          ? "glass"
          : "bg-gradient-to-b from-white/[0.022] to-white/[0.005]",
        glow &&
          "relative overflow-hidden before:pointer-events-none before:absolute before:inset-0 before:bg-[radial-gradient(70%_60%_at_80%_0%,rgba(255,179,112,0.14),transparent_60%)]",
        padMap[padding],
        className,
      )}
    >
      {glow ? <div className="relative z-10">{children}</div> : children}
    </div>
  );
}

// ── Wave — animated voice-activity bars ────────────────────────────────────
// Decorative; conveys "audio is live". Pure CSS, no animation frame.

interface WaveProps {
  bars?: number;
  className?: string;
  /** Color of the bars — amber by default. */
  tone?: "amber" | "t1" | "t3";
  /** Height of the wave container in px (bars scale within). */
  height?: number;
}

export function Wave({
  bars = 6,
  className,
  tone = "amber",
  height = 16,
}: WaveProps) {
  const bgClass = {
    amber: "bg-amber",
    t1: "bg-t1",
    t3: "bg-t3",
  }[tone];

  return (
    <div
      className={cx("inline-flex items-center gap-[2px]", className)}
      style={{ height }}
      aria-hidden="true"
    >
      {Array.from({ length: bars }).map((_, i) => (
        <span
          key={i}
          className={cx("w-[2px] rounded-[1px] animate-wave-tiny", bgClass)}
          style={{ animationDelay: `${i * 0.1}s` }}
        />
      ))}
    </div>
  );
}
